/* eslint-disable react-hooks/exhaustive-deps */
import React, { Fragment, useContext, useEffect, useState } from 'react';
import {
	ApButtonIcon,
	ApButtonLink,
	ApCheckbox,
	ApLoaderDefault,
	ApLoaderText,
	ApIcon,
	colors,
	ApChip,
} from '@alixpartners/ui-components';
import { Tooltip } from 'react-tippy';
import debounce from 'lodash/debounce';
import { StyledModal, Wrapper } from './SearchDialog.styles';
import { MoveToBinViewModal } from 'Details/MoveToBinModal/MoveToBinModal';
import { PERMISSIONS, PermissionsContext } from 'contexts/PermissionsContext';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SpendCubeSettingsContext } from 'contexts/SpendCubeSettingsContext';
import { SettingsContext } from 'contexts/SettingsContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { FilterChip, FilterChipGroup } from 'components/FilterChip';
import { getCurrencySymbolFromOptions } from 'Details/utils';
import { convertToArray, UNITS_MAP } from 'Details/utils';
import {
	getSearchResults,
	getTransactionsBinDimensions,
	moveMultipleToBin,
} from 'utils/apiRequests';
import { getLocalStorageItem, setLocalStorageItem } from 'utils/localStorage';
import { DimensionDropdown } from './DimensionDropdown';
import {
	usePrevious,
	SPEND_TYPE_LABELS,
	SPEND_TYPE,
	TAXONOMY_TYPES,
} from 'Investigate/utils';
import {
	getCols,
	getColumnValue,
	getTags,
	SELECTED_DIMENSIONS_LOCAL_STORAGE_KEY,
	getRequestParams,
	getHierarchy,
} from './utils';
import {
	Container,
	FiltersContainer,
	FiltersLabel,
	ChipsContainer,
	NavigateLink,
	Selection,
	QuickSearchContainer,
	QuickSearchLabel,
	QuickSearchOptions,
	TableHeader,
	DialogSearchWrapper,
	StyledSearch,
	TableHeaderCol,
	TableRows,
	TableRow,
	TableCell,
	TableNameCell,
	Pagination,
	PaginationText,
	DeleteButton,
	TooltipRow,
	EllipsisSpan,
	TooltipContent,
} from './SearchDialog.styles';
import { isEqual, uniqBy } from 'lodash';

const getTop3Selected = (prevSelectedItems = [], item) => {
	const newSelected = uniqBy(
		[
			...prevSelectedItems,
			{
				...item,
				date: new Date(),
			},
		],
		'value'
	);
	if (newSelected.length <= 3) {
		return newSelected;
	}
	return newSelected.splice(0, 3);
};

const DEFAULT_SORTING = {
	columnIndex: 3,
	direction: 'DESC',
};

export const SearchDialog = props => {
	const queryStringState = useContext(QueryStringContext);
	const { options } = useContext(SettingsOptionsContext);
	const { permissions } = useContext(PermissionsContext);
	const { currency: currencyList } = options;

	const { currency } = queryStringState;
	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);
	const [itemsOnPage, addItemsOnPage] = useState(25);
	const [shouldRefetch, setShouldRefetch] = useState(true);
	const [isLoading, setLoading] = useState(false);
	const [dimensions, setDimensions] = useState([]);
	const [selectedDimension, setSelectedDimension] = useState(null);
	const [prevSelectedDimensions, setPrevSelectedDimensions] = useState([]);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isMoveToBinOpen, setIsMoveToBinOpen] = useState(false);
	const [search, setSearch] = React.useState('');
	const [transactions, setTransactions] = React.useState(null);
	const [selectedTransactions, setSelectedTransactions] = useState([]);
	const [isAllSelectedTransactions, setIsAllSelectedTranscations] = useState(
		false
	);
	// Make default sorting by spend
	const [sorting, setSorting] = useState(DEFAULT_SORTING);
	const [quickFilter, setQuickFilter] = React.useState(undefined);
	const onSearchChange = query => {
		setQuickFilter(0);
		setSearch(query);
	};
	const onSearchChangeDebounced = debounce(onSearchChange, 200);

	const onSearchClear = () => {
		setSearch('');
	};

	const params = getRequestParams({
		...props,
		selectedDimension,
		currency,
		itemsOnPage,
		quickFilter,
		search,
		sorting,
	});

	const prevParams = usePrevious(params);

	useEffect(
		() => {
			if (permissions && permissions.includes(PERMISSIONS.TransactionsWrite)) {
				getTransactionsBinDimensions().then(result => {
					setDimensions(result);
				});
			}
			const localStorageDimensions = getLocalStorageItem(
				SELECTED_DIMENSIONS_LOCAL_STORAGE_KEY
			);
			if (localStorageDimensions) {
				setPrevSelectedDimensions(localStorageDimensions);
			}
		},
		[permissions]
	);

	// If outer params or sorting has been changed set shouldRefresh flag
	useEffect(
		() => {
			if (params && !isEqual(prevParams, params)) {
				setShouldRefetch(true);
			}
		},
		[params, prevParams]
	);

	// If modal is open and should shouldRefresh is true then fetch transactions
	useEffect(
		() => {
			if (isDialogOpen && shouldRefetch && params) {
				setLoading(true);
				getSearchResults(params).then(result => {
					setTransactions(result);
					setShouldRefetch(false);
					setLoading(false);
				});
			}
		},
		[shouldRefetch, isDialogOpen]
	);

	const prevSelectedDimension = usePrevious(selectedDimension);

	useEffect(
		() => {
			if (!isEqual(prevSelectedDimension, selectedDimension)) {
				setSelectedTransactions([]);
				setIsAllSelectedTranscations(false);
			}
		},
		[selectedDimension]
	);

	useEffect(
		() => {
			if (!selectedDimension && dimensions.length) {
				setSelectedDimension({
					...dimensions[0],
					label: dimensions[0].displayName.replaceAll('_', ' '),
					value: dimensions[0].displayName,
				});
			}
		},
		[dimensions, selectedDimension]
	);

	const moveToBin = () => {
		const params = getRequestParams({
			...props,
			selectedDimension,
			currency,
			itemsOnPage,
			quickFilter,
			search,
			sorting,
		});

		if (params) {
			const selectedItems = selectedTransactions.map(transaction => {
				return {
					dimensionName: selectedDimension.dimensionName,
					value: transaction.value,
					dimensionValues: transaction.dimensionValues,
				};
			});

			moveMultipleToBin(params, selectedItems).then(() => {
				setShouldRefetch(true);
				setIsMoveToBinOpen(false);
				setSelectedTransactions([]);
			});
		}
	};

	const updateQuickFilterSelection = filter => {
		if (quickFilter === filter) {
			setQuickFilter(0);
		} else {
			onSearchClear();
			setQuickFilter(filter);
		}
	};

	const handleTransactionsChange = transaction => {
		let updatedItems = [];
		const currentIndex = selectedTransactions.findIndex(item =>
			isEqual(transaction.dimensionValues, item.dimensionValues)
		);

		if (currentIndex === -1) {
			updatedItems = [...selectedTransactions, transaction];

			if (updatedItems.length === transactions.totalCount) {
				setIsAllSelectedTranscations(true);
			}
		} else {
			updatedItems = [
				...selectedTransactions.slice(0, currentIndex),
				...selectedTransactions.slice(currentIndex + 1),
			];
			setIsAllSelectedTranscations(false);
		}

		setSelectedTransactions(updatedItems);
	};

	const isTransactionSelected = row => {
		return !!selectedTransactions.find(item =>
			isEqual(row.dimensionValues, item.dimensionValues)
		);
	};

	const tags = getTags(props);
	const columns = getCols(transactions, selectedDimension);
	const firstColumn = columns && columns.length ? columns.shift() : null;

	const shouldIncludeCustomCategories = () => {
		const { spendType, indirectTaxonomy, directTaxonomy } = props;
		const indirectTypeNumber = Number(indirectTaxonomy);
		const directTypeNumber = Number(directTaxonomy);

		if (!spendType) {
			return false;
		}

		if (Number(spendType) === SPEND_TYPE.INDIRECT) {
			return indirectTypeNumber === TAXONOMY_TYPES.CLIENT;
		}

		return directTypeNumber === TAXONOMY_TYPES.CLIENT;
	};

	const handleViewTransactionsClick = row => {
		const { showExplorePage, spendType, getTailSpendOnly, hierarchy } = props;
		const options = {};

		if (shouldIncludeCustomCategories()) {
			options.isCustom = true;
		}

		if (spendType) {
			options.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}

		if (selectedDimension && selectedDimension.columnName) {
			options.selectedDimensionKey = selectedDimension.columnName;
			options.selectedDimensionName = row.value;
		}

		if (getTailSpendOnly) {
			options.isTailSpend = true;
		}

		if (hierarchy && hierarchy.length > 0) {
			options.hierarchy = hierarchy;
		}

		showExplorePage(options, row.value);
	};

	const selectedTranscationsAmount = selectedTransactions.length
		? selectedTransactions.reduce((acc, transaction) => {
				acc += transaction.transactionsCount;

				return acc;
		  }, 0)
		: 0;

	return (
		<Fragment>
			<ApButtonIcon
				iconName="search"
				onClick={() => {
					setIsDialogOpen(true);
				}}
			/>
			<Wrapper onClick={e => e.stopPropagation()}>
				<StyledModal
					isOpen={isDialogOpen}
					header={
						<FiltersContainer>
							<FiltersLabel>Search by:</FiltersLabel>
							<ChipsContainer>
								{tags.map((tag, i) => (
									<ApChip small key={i}>
										{tag}
									</ApChip>
								))}
							</ChipsContainer>
						</FiltersContainer>
					}
					isDismissible
					hasClosed={() => {
						setIsDialogOpen(false);
						setSelectedTransactions([]);
						setQuickFilter();
						onSearchClear();
						setSorting(DEFAULT_SORTING);
					}}
					footer={() => {
						return <div />;
					}}
				>
					<Container>
						<Selection>
							<DimensionDropdown
								isLoading={isLoading}
								options={dimensions.map(dimension => ({
									...dimension,
									label: dimension.displayName.replaceAll('_', ' '),
									value: dimension.displayName,
								}))}
								selected={selectedDimension || { label: 'none' }}
								onChange={option => {
									setLocalStorageItem(
										SELECTED_DIMENSIONS_LOCAL_STORAGE_KEY,
										getTop3Selected([...prevSelectedDimensions], option)
									);
									setSelectedDimension(option);
								}}
								prevSelectedDimensions={prevSelectedDimensions}
							/>
							<DialogSearchWrapper>
								<StyledSearch
									onChange={onSearchChangeDebounced}
									onClear={onSearchClear}
									value={search}
								/>
							</DialogSearchWrapper>
						</Selection>
						<QuickSearchContainer>
							<QuickSearchLabel>Quick search options:</QuickSearchLabel>
							<QuickSearchOptions>
								<FilterChipGroup>
									<FilterChip
										isSelected={quickFilter === 'Empty / Null'}
										label="Empty / Null"
										onClick={() => updateQuickFilterSelection('Empty / Null')}
									/>
									<FilterChip
										isSelected={quickFilter === 'Contains numerical'}
										label="Contains numerical"
										onClick={() =>
											updateQuickFilterSelection('Contains numerical')
										}
									/>
									<FilterChip
										isSelected={quickFilter === 'Numerical only'}
										label="Numerical only"
										onClick={() => updateQuickFilterSelection('Numerical only')}
									/>
									<FilterChip
										isSelected={quickFilter === 'Special characters'}
										label="Special characters"
										onClick={() =>
											updateQuickFilterSelection('Special characters')
										}
									/>
								</FilterChipGroup>
							</QuickSearchOptions>
						</QuickSearchContainer>
						{transactions && transactions.totalCount != null ? (
							<Fragment>
								<TableHeader>
									<TableHeaderCol width={64}>
										<ApCheckbox
											onChange={() => {
												if (isAllSelectedTransactions) {
													setSelectedTransactions([]);
												} else {
													setSelectedTransactions(transactions.body);
												}
												setIsAllSelectedTranscations(
													!isAllSelectedTransactions
												);
											}}
											checked={isAllSelectedTransactions}
										/>
									</TableHeaderCol>
									{selectedTransactions.length ? (
										<Fragment>
											<TableHeaderCol
												align="left"
												width={500}
												noMaxWidth
												onClick={() =>
													setSorting({
														columnIndex: 1,
														direction:
															sorting.direction === 'ASC' ? 'DESC' : 'ASC',
													})
												}
											>
												<span>
													{selectedTransactions.length} items selected ({
														selectedTranscationsAmount
													}{' '}
													transactions)
												</span>
											</TableHeaderCol>
											<DeleteButton onClick={() => setIsMoveToBinOpen(true)}>
												Move {selectedTransactions.length} items to bin
											</DeleteButton>
										</Fragment>
									) : (
										<Fragment>
											<TableHeaderCol
												align={firstColumn.align}
												width={firstColumn.width}
												onClick={() =>
													setSorting({
														columnIndex: firstColumn.sortIndex,
														direction:
															sorting.direction === 'ASC' ? 'DESC' : 'ASC',
													})
												}
											>
												<span>{firstColumn.title}</span>
												{firstColumn.sortIndex === sorting.columnIndex && (
													<ApIcon
														iconName={
															sorting.direction === 'ASC'
																? 'arrow_upward'
																: 'arrow_downward'
														}
														iconColor={colors.grey4}
														iconSize={16}
													/>
												)}
											</TableHeaderCol>
											{columns.map(column => (
												<TableHeaderCol
													key={`header-${column.colName}`}
													align={column.align}
													width={column.width}
													onClick={() =>
														setSorting({
															columnIndex: column.sortIndex,
															direction:
																sorting.direction === 'ASC' ? 'DESC' : 'ASC',
														})
													}
												>
													<span>{column.title}</span>
													{column.sortIndex === sorting.columnIndex && (
														<ApIcon
															iconName={
																sorting.direction === 'ASC'
																	? 'arrow_upward'
																	: 'arrow_downward'
															}
															iconColor={colors.grey4}
															iconSize={16}
														/>
													)}
												</TableHeaderCol>
											))}
											<TableHeaderCol
												width={transactions.body.length > 9 ? 64 : 48}
											/>
										</Fragment>
									)}
								</TableHeader>
								<TableRows>
									{transactions.body.map((row, i) => (
										<TableRow
											key={`row-${row.value}-${i}`}
											isSelected={isTransactionSelected(row)}
										>
											<TableCell width={64}>
												<ApCheckbox
													onChange={() => handleTransactionsChange(row)}
													checked={isTransactionSelected(row)}
												/>
											</TableCell>
											{firstColumn ? (
												<TableNameCell
													key={`cell-${firstColumn.colName}-${row.value}`}
													align={firstColumn.align}
													width={firstColumn.width}
												>
													{!!getHierarchy(row) ? (
														<Tooltip
															position="top"
															animation="fade"
															arrow
															html={
																<TooltipContent>
																	{getHierarchy(row)}
																</TooltipContent>
															}
														>
															<TooltipRow>
																<EllipsisSpan>
																	{getColumnValue({
																		column: firstColumn,
																		currencySymbol,
																		row,
																		search,
																	})}
																</EllipsisSpan>
															</TooltipRow>
														</Tooltip>
													) : (
														<TooltipRow>
															<EllipsisSpan>
																{getColumnValue({
																	column: firstColumn,
																	currencySymbol,
																	row,
																	search,
																})}
															</EllipsisSpan>
														</TooltipRow>
													)}
												</TableNameCell>
											) : null}
											{columns.map(column => (
												<TableCell
													key={`cell-${column.colName}-${row.value}`}
													align={column.align}
													width={column.width}
												>
													{getColumnValue({
														column,
														currencySymbol,
														row,
														search,
													})}
												</TableCell>
											))}
											<TableCell align="left" width={48}>
												<NavigateLink
													iconName="trending_flat"
													onClick={() => {
														setIsDialogOpen(false);
														handleViewTransactionsClick(row);
													}}
												/>
											</TableCell>
										</TableRow>
									))}
								</TableRows>

								{transactions &&
									transactions.totalCount > 25 && (
										<Pagination>
											<PaginationText>
												{itemsOnPage}/{transactions.totalCount}
											</PaginationText>
											{itemsOnPage < transactions.totalCount && (
												<ApButtonLink
													onClick={() => {
														setIsAllSelectedTranscations(false);
														addItemsOnPage(
															Math.min(
																itemsOnPage + 10,
																transactions.totalCount
															)
														);
													}}
												>
													show more
												</ApButtonLink>
											)}
										</Pagination>
									)}
							</Fragment>
						) : (
							<ApLoaderDefault loaderText={() => <ApLoaderText />} />
						)}
					</Container>
				</StyledModal>
				<MoveToBinViewModal
					isOpen={isMoveToBinOpen}
					onClose={() => setIsMoveToBinOpen(false)}
					moveToBin={moveToBin}
				/>
			</Wrapper>
		</Fragment>
	);
};

export default props => (
	<SpendCubeSettingsContext.Consumer>
		{({ settings }) => (
			<SettingsOptionsContext.Consumer>
				{({ options }) => (
					<SettingsContext.Consumer>
						{({ customFilters: customFilterOptions }) => (
							<QueryStringContext.Consumer>
								{({
									currency,
									currencyType,
									customFilters,
									customFiltersValues,
									dateFrom,
									dateTo,
									hierarchy,
									showNonSourceable,
									showNegativeSpend,
									storedQueryId,
									storedQuery,
									unit,
									setQueryStringStateAndPush,
									spendType,
									tailSpendLevel,
									directTaxonomy,
									indirectTaxonomy,
								}) => {
									const { currency: currencyList } = options;

									return (
										<SearchDialog
											currency={currency}
											currencySymbol={getCurrencySymbolFromOptions(
												currencyList,
												currency
											)}
											currencyType={currencyType}
											dateFrom={dateFrom}
											dateTo={dateTo}
											customFilters={convertToArray(customFilters)}
											customFiltersValues={convertToArray(customFiltersValues)}
											customFilterOptions={customFilterOptions}
											hierarchy={convertToArray(hierarchy)}
											showNonSourceable={showNonSourceable}
											scaleSymbol={unit || ''}
											scaleTier={UNITS_MAP[unit] || UNITS_MAP.m}
											showNegativeSpend={
												!!showNegativeSpend && showNegativeSpend !== 'false'
											}
											storedQueryId={storedQueryId}
											storedQuery={storedQuery}
											getTailSpendOnly={tailSpendLevel != null}
											useNetSpend={
												settings && settings.tailSpend
													? settings.tailSpend.useNetSpend
													: true
											}
											unit={unit}
											setQueryStringStateAndPush={setQueryStringStateAndPush}
											spendType={spendType}
											directTaxonomy={directTaxonomy}
											indirectTaxonomy={indirectTaxonomy}
											{...props}
										/>
									);
								}}
							</QueryStringContext.Consumer>
						)}
					</SettingsContext.Consumer>
				)}
			</SettingsOptionsContext.Consumer>
		)}
	</SpendCubeSettingsContext.Consumer>
);
